/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';

import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { DateField } from './DateField';
import type { CalendarDate, DateGranularity, EditableDateSegment } from './types';

type DateFieldType = ComponentProps<typeof DateField>;
type DateSegmentState = {
  'aria-valuenow': number | undefined;
  isComplete: boolean;
  isSelected: boolean;
  type: EditableDateSegment;
};
type DateFieldState = {
  readonly currentDate: CalendarDate | undefined;
  readonly isDateSelected: boolean;
  readonly segments: DateSegmentState[];
};

type Props = {
  granularity: DateGranularity;
  locale: BCP47Locale;
  value: DateFieldType['value'];
};

/**
 * The initial state of the date field can be passed to the dateReducer when the component
 * mounts, and can also be used to reset the state when the value changes programmatically.
 *
 * @param props - An object containing the properties used to derive the initial state.
 * @param props.granularity
 * @param props.value The CalendarDate value
 * @returns The initial state of the date field that can be passed to the dateReducer.
 */
const getDateFieldInitialReducerState = ({ granularity, value: currentValue }: Props) => {
  // Derive some data from currentValue that will become part of our initial state.
  const yearValue = currentValue?.year;
  const monthValue = currentValue?.month;
  const dayValue = currentValue?.day;

  // 'aria-valuenow' is the segment's value. In the dateReducer we look at the 'aria-valuenow'
  // for each segment and when a user updates one segment, that new value may affect the others.
  // In the dateReducer we have a currentDate state that is calculated at the end of the dispatch action
  // - it takes each segment's 'aria-valuenow' and creates the currentDate from it.
  const daySeg = [
    {
      type: 'day' as EditableDateSegment,
      'aria-valuenow': dayValue,
      isComplete: !!dayValue,
      isSelected: false
    }
  ];

  // Create state for each editable segment of a date, i.e. year, month, or day.
  const editableSegments = [
    {
      type: 'year' as EditableDateSegment,
      'aria-valuenow': yearValue,
      isComplete: !!yearValue,
      isSelected: false
    },
    {
      type: 'month' as EditableDateSegment,
      'aria-valuenow': monthValue,
      isComplete: !!monthValue,
      isSelected: false
    },
    ...(granularity === 'day' ? daySeg : [])
  ];

  // Create the initial state to pass to our reducer.
  const initialState: DateFieldState = {
    currentDate: currentValue,
    isDateSelected: false,
    segments: editableSegments
  };

  return initialState;
};

export { getDateFieldInitialReducerState };
export type { DateFieldState, DateSegmentState };
